import type { AppDispatch } from "../../../common/state/store";
import {
  setInstallJob,
  updateInstallProgress,
  updateInstallStatus,
  updateInstallTotalSize,
  updateAgentJobId,
} from "../state/deviceInstallerSlice";
import { AGENT_BASE_URL, AGENT_WS_URL } from "../constants";

interface InstallViaAgentParams {
  appId: string;
  displayName: string;
  packageName: string;
  s3Url: string;
  version: string;
  fileName: string;
}

interface AgentErrorResponse {
  error: string;
}

interface AgentInstallStartResponse {
  jobId: string;
}

interface AgentInstallProgressData {
  status: "downloading" | "extracting" | "installing" | "success" | "error";
  progress: number;
  totalSizeMB?: number;
  errorMessage?: string;
  version?: string;
}

interface AgentInstallProgressMessage {
  type: "progress" | "complete" | "error";
  appId: string;
  jobId: string;
  data: AgentInstallProgressData;
}

let activeWebSocket: WebSocket | null = null;

/** Cancel an in-progress agent install */
export function cancelAgentInstall(jobId: string): void {
  fetch(`${AGENT_BASE_URL}/install/${jobId}`, { method: "DELETE" }).catch(() => {});
  if (activeWebSocket) {
    activeWebSocket.close();
    activeWebSocket = null;
  }
}

/** Install an app via the desktop agent, dispatching Redux actions for progress */
export async function installViaAgent(
  params: InstallViaAgentParams,
  dispatch: AppDispatch,
): Promise<string> {
  const { appId, displayName, packageName, s3Url, version, fileName } = params;

  dispatch(
    setInstallJob({
      fileName,
      packageName,
      agentAppId: appId,
      status: "downloading",
      progress: 0,
    }),
  );

  const res = await fetch(`${AGENT_BASE_URL}/install`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ appId, displayName, packageName, s3Url, version, fileName }),
  });

  if (!res.ok) {
    const err = (await res
      .json()
      .catch(() => ({ error: "Agent request failed" }))) as AgentErrorResponse;
    dispatch(updateInstallStatus({ status: "error", errorMessage: err.error }));
    throw new Error(err.error);
  }

  const { jobId } = (await res.json()) as AgentInstallStartResponse;
  dispatch(updateAgentJobId(jobId));

  return new Promise<string>((resolve, reject) => {
    const ws = new WebSocket(AGENT_WS_URL);
    activeWebSocket = ws;

    ws.onmessage = (event) => {
      let msg: AgentInstallProgressMessage;
      try {
        msg = JSON.parse(event.data as string) as AgentInstallProgressMessage;
      } catch (err) {
        console.warn("[DesktopAgent] unreadable install progress message:", err);
        return;
      }
      if (msg.jobId !== jobId) return;

      const { status, progress, totalSizeMB, errorMessage, version: completedVersion } = msg.data;

      if (totalSizeMB) {
        dispatch(updateInstallTotalSize(totalSizeMB));
      }

      switch (status) {
        case "downloading":
          dispatch(updateInstallProgress(progress));
          break;
        case "extracting":
          dispatch(updateInstallStatus({ status: "extracting" }));
          break;
        case "installing":
          dispatch(updateInstallStatus({ status: "installing" }));
          break;
        case "success":
          dispatch(updateInstallStatus({ status: "success" }));
          ws.close();
          activeWebSocket = null;
          resolve(completedVersion || version);
          break;
        case "error":
          dispatch(updateInstallStatus({ status: "error", errorMessage }));
          ws.close();
          activeWebSocket = null;
          reject(new Error(errorMessage));
          break;
      }
    };

    ws.onerror = () => {
      dispatch(updateInstallStatus({ status: "error", errorMessage: "Connection to agent lost" }));
      activeWebSocket = null;
      reject(new Error("WebSocket error"));
    };

    ws.onclose = () => {
      activeWebSocket = null;
    };
  });
}

/** Launch an app via the desktop agent */
export async function launchViaAgent(appId: string): Promise<void> {
  const res = await fetch(`${AGENT_BASE_URL}/launch/${appId}`, { method: "POST" });
  if (!res.ok) {
    const err = (await res.json().catch(() => ({ error: "Launch failed" }))) as AgentErrorResponse;
    throw new Error(err.error);
  }
}

/** Uninstall an app via the desktop agent */
export async function uninstallViaAgent(appId: string): Promise<void> {
  const res = await fetch(`${AGENT_BASE_URL}/uninstall/${appId}`, { method: "DELETE" });
  if (!res.ok) {
    const err = (await res
      .json()
      .catch(() => ({ error: "Uninstall failed" }))) as AgentErrorResponse;
    throw new Error(err.error);
  }
}

/** Open an app's install folder in the OS file manager */
export async function openFolderViaAgent(appId: string): Promise<void> {
  const res = await fetch(`${AGENT_BASE_URL}/agent/open-folder/${appId}`, { method: "POST" });
  if (!res.ok) {
    const err = (await res
      .json()
      .catch(() => ({ error: "Failed to open folder" }))) as AgentErrorResponse;
    throw new Error(err.error);
  }
}

/** Fully uninstall the desktop agent (removes autostart, apps, data, binary) */
export async function uninstallAgent(): Promise<void> {
  await fetch(`${AGENT_BASE_URL}/agent/uninstall-self`, { method: "POST" }).catch(() => {});
}
